import { useState, useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { PricingProvider } from './context/PricingContext';

// Layout
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Engagement
import FloatingWhatsApp from './components/FloatingWhatsApp';
import LeadPopup from './components/LeadPopup';

// Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import IGCSEPage from './pages/IGCSEPage';
import ALevelsPage from './pages/ALevelsPage';
import SubjectDetailPage from './pages/SubjectDetailPage';
import ResultsPage from './pages/ResultsPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import ReturnPolicyPage from './pages/ReturnPolicyPage';

// Loading Spinner
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-surface">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aspire-red mx-auto mb-4"></div>
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Protected Route using Supabase Auth
const ProtectedRoute = ({ children }) => {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { state: { from: location }, replace: true });
    }
  }, [user, loading, navigate, location]);

  if (loading) return <LoadingSpinner />;
  if (!user) return null;

  return children;
};

function AppRoutes() {
  const location = useLocation();
  const isPrivatePage = ['/login', '/register', '/dashboard', '/admin'].some(p =>
    location.pathname.startsWith(p)
  );

  return (
    <>
    <Routes>
      {/* Public Pages */}
      <Route path="/" element={<><Header /><HomePage /><Footer /></>} />
      <Route path="/about" element={<><Header /><AboutPage /><Footer /></>} />
      <Route path="/igcse" element={<><Header /><IGCSEPage /><Footer /></>} />
      <Route path="/igcse/:slug" element={<><Header /><SubjectDetailPage /><Footer /></>} />
      <Route path="/a-levels" element={<><Header /><ALevelsPage /><Footer /></>} />
      <Route path="/a-levels/:slug" element={<><Header /><SubjectDetailPage /><Footer /></>} />
      <Route path="/results" element={<><Header /><ResultsPage /><Footer /></>} />
      <Route path="/contact" element={<><Header /><ContactPage /><Footer /></>} />
      <Route path="/privacy-policy" element={<><Header /><PrivacyPolicyPage /><Footer /></>} />
      <Route path="/terms" element={<><Header /><TermsPage /><Footer /></>} />
      <Route path="/return-policy" element={<><Header /><ReturnPolicyPage /><Footer /></>} />

      {/* Auth Pages */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Protected Pages */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Header /><DashboardPage /><Footer />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Header /><AdminDashboardPage /><Footer />
        </ProtectedRoute>
      } />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    {!isPrivatePage && <FloatingWhatsApp />}
    {!isPrivatePage && <LeadPopup />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <PricingProvider>
            <Toaster position="top-right" richColors />
            <AppRoutes />
            <Analytics />
            <SpeedInsights />
          </PricingProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
